<template>
  <v-card
    variant="outlined"
    class="pa-3"
  >
    <div class="d-flex align-center mb-2">
      <strong class="text-body-2">Talk to a human</strong>
      <v-spacer />
      <v-btn
        icon="mdi-close"
        variant="text"
        size="x-small"
        aria-label="Close"
        @click="$emit('close')"
      />
    </div>

    <template v-if="state === 'created'">
      <p class="text-body-2 mb-0">
        Your request was sent to WireSock support<template v-if="ticketNumber"> (ticket #{{ ticketNumber }})</template>.
        We will reply by email. Add license keys or logs directly in the ticket, not in this chat.
      </p>
    </template>

    <template v-else-if="state === 'confirmation-sent'">
      <p class="text-body-2 mb-0">
        We sent a confirmation link to <strong>{{ email }}</strong>. Open it to create your support request —
        it includes this conversation, so you don't have to repeat it.
      </p>
    </template>

    <template v-else-if="state === 'fallback'">
      <p class="text-body-2 mb-1">The helpdesk is not reachable right now. Please email us instead:</p>
      <a :href="mailto">support@wiresock.net</a>
    </template>

    <form
      v-else
      @submit.prevent="submit"
    >
      <p class="text-body-2 mb-2">
        We will create a support request with this conversation attached.
        <template v-if="!signedIn">Enter your email — we will send a link to confirm it first.</template>
      </p>
      <v-text-field
        v-if="!signedIn"
        v-model="email"
        type="email"
        label="Your email"
        variant="outlined"
        density="compact"
        autocomplete="email"
        :error-messages="error ? [ error ] : []"
        class="mb-2"
      />
      <v-alert
        v-else-if="error"
        type="warning"
        variant="tonal"
        density="compact"
        class="mb-2"
      >{{ error }}</v-alert>
      <v-btn
        type="submit"
        color="primary"
        variant="flat"
        size="small"
        class="text-none"
        :loading="sending"
        :disabled="!conversationId || (!signedIn && !email.trim())"
      >Send to support</v-btn>
      <div
        v-if="!conversationId"
        class="text-caption text-grey mt-1"
      >Ask your question first, or email <a href="mailto:support@wiresock.net">support@wiresock.net</a>.</div>
    </form>
  </v-card>
</template>

<script
  setup
  lang="ts"
>
import type { FetchError } from 'ofetch';

const props = defineProps<{ conversationId: string | null; conversationToken: string | null }>();
defineEmits<{ close: [] }>();

type EscalationResponse =
  | { status: 'created'; ticketNumber?: string }
  | { status: 'confirmation-sent' }
  | { status: 'fallback'; mailto: string };

const auth = useAuth();
const signedIn = computed(() => auth.status.value === 'authenticated');
const email = ref('');
const sending = ref(false);
const error = ref('');
const state = ref<EscalationResponse['status'] | null>(null);
const ticketNumber = ref<string | undefined>();
const mailto = ref('mailto:support@wiresock.net');

async function submit(): Promise<void> {
  if (!props.conversationId || !props.conversationToken) {
    return;
  }
  sending.value = true;
  error.value = '';
  try {
    const response = await $fetch<EscalationResponse>('/api/assistant/escalate', {
      method: 'POST',
      body: { conversationId: props.conversationId, conversationToken: props.conversationToken, email: signedIn.value ? undefined : email.value.trim() },
      headers: { 'X-CSRF-Token': (await auth.getCsrfToken()) ?? '' },
    });
    state.value = response.status;
    if (response.status === 'created') {
      ticketNumber.value = response.ticketNumber;
    }
    if (response.status === 'fallback') {
      mailto.value = response.mailto;
    }
  } catch (caught) {
    const fetchError = caught as FetchError<{ message?: string }>;
    error.value = fetchError.data?.message ?? 'Could not send the request. Please email support@wiresock.net.';
  } finally {
    sending.value = false;
  }
}
</script>
